import { useState, useEffect, useRef } from "react";
import {
    Box,
    Chip,
    CircularProgress,
    Container,
    LinearProgress,
    Stack,
    Typography,
} from "@mui/material";
import { Splashscreen, GoogleMap, Error as ErrorDialog } from "src/components";
import { useTitle } from "react-use";
import { tripsAPI } from "../redux/tripSlice";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "src/hooks";
import { authSelector } from "src/modules/auth/redux/authSlice";
import { Slide } from "react-awesome-reveal";
import { useCountDown } from "@raddix/use-count-down";

const TransitExperience = () => {
    useTitle("Transit Experience");
    const { id = "" } = useParams();
    const initCount = useRef(0);
    const navigate =  useNavigate();
    const [processing, setProcessing] = useState({
        active: false,
        showMap: false,
        countdown: false,
        spinner: false,
        count: 0,
        message: "Confirming your ride",
    });

    const { config } = useAppSelector(authSelector);
    const { data, isError, isLoading, refetch } =
        tripsAPI.useGetTransitExperienceQuery(id, {
            skip: !id,
            refetchOnFocus: true,
            refetchOnMountOrArgChange: true,
            refetchOnReconnect: true,
            pollingInterval: 15000, // 15 seconds
        });

    const [count, actions] = useCountDown(initCount.current, {
        autoStart: true,
    });

    useEffect(() => {
        if (data?.runId && !processing.countdown && !processing.showMap) {
            setProcessing((current) => ({ ...current, countdown: true }));
            setTimeout(() => {
                setProcessing((current) => ({
                    ...current,
                    showMap: true,
                    countdown: false,
                }));
            }, 7000);
        }
        if (data?.ETA && !initCount.current && !processing.spinner) {
            initCount.current = data.ETA;
            setProcessing((current) => ({
                ...current,
                spinner: true,
                count: data.ETA,
            }));
            // actions.reset();
            actions.start();
        }

        if(data && (!data.isValid || data?.isNext)) {
            navigate(`/trips/${data.id}?status=live`, { state: {} })
        }
    }, [data, actions, processing, count, navigate]);

    if (isLoading || !processing.showMap) {
        return <Splashscreen description={processing.message} />;
    }

    if (isError) {
        return (
            <ErrorDialog
                description="Sorry an error occured, please try"
                onClick={refetch}
            />
        );
    }

    return (
        <Slide direction="up" duration={1500}>
            <Container maxWidth="md" sx={{ p: 0, mt: { md: 4 } }}>
                <GoogleMap
                    loading={isLoading}
                    showDirections={false}
                    pickUpCoords={
                        data?.coords.pickUpCoords || {
                            lat: config.Location.Latitude,
                            lng: config.Location.Longitude,
                        }
                    }
                    dropOffCoords={data?.coords.dropOffCoords}
                    vehicleCoords={data?.coords.vehicleCoords}
                    sx={{ height: 450, width: "100%" }}
                />

                <Box sx={{ p: 2 }}>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        mt={4}
                    >
                        <Typography variant="h5" fontWeight={600} fontSize={22}>
                            Arrive in
                        </Typography>
                        {data?.ETADisplay ? (
                            <Chip label={data.ETADisplay} color="primary" />
                        ) : (
                            "N/A"
                        )}
                    </Stack>

                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        mt={2}
                    >
                        <Typography variant="h5" fontWeight={500} fontSize={20}>
                            {count < 3
                                ? "Just a few more seconds"
                                : "See ride details in"}
                        </Typography>
                        <Typography variant="h5" fontSize={22}>
                            {count < 3 ? (
                                <CircularProgress
                                    color="primary"
                                    disableShrink
                                    sx={{ animationDuration: "3s" }}
                                />
                            ) : (
                                `${count} sec`
                            )}
                        </Typography>
                    </Stack>
                    {count > 0 && (
                        <LinearProgress
                            color="primary"
                            variant="determinate"
                            value={100 - (count / initCount.current) * 100}
                            sx={{ height: 8, mt: 2 }}
                        />
                    )}
                </Box>
            </Container>
        </Slide>
    );
};

export default TransitExperience;
